import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { PAGE_TYPES, PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import {
	parseAmenityFromPath,
	parseVehicleFromPath,
	parseSizeFromPath,
	getSqftForSize,
} from 'lib/search/parseAmenityFromPath';

export function getSearchQuery(nextProps) {
	let query;

	if (nextProps.queryParams?.page > 1) {
		// handles pagination for CLP, Amenity, and Vehicle pages
		query = { ...nextProps.queryParams };

		// For amenity landing pages on pagination, extract amenity from the URL path
		// if it's not already in the query params (since we're now omitting it from pagination links)
		if (
			nextProps.pageType === PAGE_SUB_TYPES.AMENITY_LANDING &&
			!query.amenity
		) {
			const amenityFromPath = parseAmenityFromPath(
				nextProps.location?.pathname,
			);
			if (amenityFromPath) {
				query.amenity = amenityFromPath;
			}
		}

		// For vehicle landing pages on pagination, extract vehicle from the URL path
		// if it's not already in the query params (since we're now omitting it from pagination links)
		if (
			nextProps.pageType === PAGE_SUB_TYPES.VEHICLE_LANDING &&
			!query.vehicle
		) {
			const vehicleFromPath = parseVehicleFromPath(
				nextProps.location?.pathname,
			);
			if (vehicleFromPath) {
				query.searchType = 'vehicle';
				query.vehicle = vehicleFromPath;
			}
		}

		// For size landing pages on pagination, extract sqft from the URL path
		// if it's not already in the query params (since we're now omitting it from pagination links)
		const sizePageTypes = [
			'city_5x5_landing_page',
			'city_5x10_landing_page',
			'city_5x15_landing_page',
			'city_10x10_landing_page',
			'city_10x15_landing_page',
			'city_10x20_landing_page',
			'city_10x25_landing_page',
			'city_10x30_landing_page',
			'city_mini_storage_landing_page',
			'city_10x30_landing_page',
		];

		if (sizePageTypes.includes(nextProps.pageType)) {
			// Extract size from pageType (e.g., "5x5" from "city_5x5_landing_page")
			// Mini-storage doesn't follow the numeric pattern, so handle it explicitly
			let landingPageSize;
			if (nextProps.pageType === 'city_mini_storage_landing_page') {
				landingPageSize = 'mini-storage';
			} else {
				const sizeMatch = nextProps.pageType.match(/city_([0-9x]+)_landing_page/);
				landingPageSize = sizeMatch ? sizeMatch[1] : null;
			}

			// Add the size context that API Gateway needs to build proper canonical URLs
			// Use 'sizeLandingPage' as subPageType for all size landing pages (SF-19305)
			// This ensures ContentMetaUtil preserves the specific size label during pagination
			// This needs to stay as sizeLandingPage here to retain for pagination
			// Resolves the issue of non-canonical meta updates being forced on pagination of 
			// size landing pages. This is intentional
			if (landingPageSize) {
				query.landingPageSize = landingPageSize;
				query.subPageType = 'sizeLandingPage';
			}

			// Extract sqft from path if not in query params
			if (!query.sqft) {
				const sizeFromPath = parseSizeFromPath(
					nextProps.location?.pathname,
				);
				if (sizeFromPath) {
					const sqftValue = getSqftForSize(sizeFromPath);
					if (sqftValue) {
						query.sqft = sqftValue;
					}
				}
			}
		}
	} else {
		switch (nextProps.pageType) {
			case PAGE_TYPES.CITY_LANDING:
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					subPageType: 'cityLandingPage',
					amenity: nextProps.match.params?.amenity,
					...nextProps.queryParams,
				};

				break;
			case PAGE_SUB_TYPES.ZIP_LANDING:
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					zip: nextProps.match.params.zip,
					subPageType: PAGE_SUB_TYPES.ZIP_LANDING,
					...nextProps.queryParams,
				};
				break;
			case PAGE_SUB_TYPES.NEIGHBORHOOD_LANDING:
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					neighborhood: nextProps.match.params.neighborhood,
					subPageType: PAGE_SUB_TYPES.NEIGHBORHOOD_LANDING,
					...nextProps.queryParams,
				};
				break;
			case PAGE_SUB_TYPES.AMENITY_LANDING:
				query = {
					amenity: nextProps.match.params.amenity,
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					subPageType: PAGE_SUB_TYPES.AMENITY_LANDING,
					...nextProps.queryParams,
				};
				break;
			case PAGE_SUB_TYPES.SIZE_LANDING:
				query = {
					size: nextProps.match.params.size,
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					subPageType: PAGE_SUB_TYPES.SIZE_LANDING,
					...nextProps.queryParams,
				};
				break;
			case PAGE_SUB_TYPES.VEHICLE_LANDING:
				query = {
					searchType: 'vehicle',
					vehicle: nextProps.match.params.vehicle,
					size: nextProps.match.params.size,
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					subPageType: PAGE_SUB_TYPES.VEHICLE_LANDING,
					...nextProps.queryParams,
				};
				break;
			case 'city_5x5_landing_page':
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					landingPageSize: '5x5',
					subPageType: 'city_5x5_landing_page',
					...nextProps.queryParams,
				};
				break;
			case 'city_10x10_landing_page':
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					landingPageSize: '10x10',
					subPageType: 'city_10x10_landing_page',
					...nextProps.queryParams,
				};
				break;
			case 'city_5x10_landing_page':
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					landingPageSize: '5x10',
					subPageType: 'city_5x10_landing_page',
					...nextProps.queryParams,
				};
				break;
			case 'city_5x15_landing_page':
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					landingPageSize: '5x15',
					subPageType: 'city_5x15_landing_page',
					...nextProps.queryParams,
				};
				break;
			case 'city_10x15_landing_page':
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					landingPageSize: '10x15',
					subPageType: 'city_10x15_landing_page',
					...nextProps.queryParams,
				};
				break;
			case 'city_10x20_landing_page':
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					landingPageSize: '10x20',
					subPageType: 'city_10x20_landing_page',
					...nextProps.queryParams,
				};
				break;
			case 'city_10x30_landing_page':
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					landingPageSize: '10x30',
					subPageType: 'city_10x30_landing_page',
					...nextProps.queryParams,
				};
				break;
			case 'city_10x25_landing_page':
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					landingPageSize: '10x25',
					subPageType: 'city_10x25_landing_page',
					...nextProps.queryParams
				};
				break;
			case 'city_mini_storage_landing_page':
				query = {
					city: nextProps.match.params.city,
					state: nextProps.match.params.state,
					location: `${nextProps.match.params.city}, ${nextProps.match.params.state}`,
					landingPageSize: 'mini-storage',
					subPageType: 'city_mini_storage_landing_page',
					...nextProps.queryParams
				};
				break;
			case PAGE_SUB_TYPES.STORAGE_NEAR_ME_LANDING:
			case PAGE_SUB_TYPES.STORAGE_DEALS_LANDING: {
				const hasQueryParams = Object.keys(nextProps.queryParams || {}).length > 0;
				if (hasQueryParams) {
					query = nextProps.queryParams;
				} else {
					query = {
						...nextProps.searchParams,
						subPageType: 'nearMePages',
						page: 1,
					};
				}
				break;
			}
			default:
				query = nextProps.queryParams;
		}
	}
	return query;
}

let searchHydrated = false;

// Handles Updating Search Results on Route Changes
const withSearchPageHandlers = (WrappedComponent) => {
	const WithSearchPageHandlers = (props) => {
		const {
			onSearchSuccess = noop,
			actions = { fetchSearchResults: noop },
			match = { params: {} },
			location,
			isStorageDealsPage,
			...rest
		} = props;

		const prevLocationKeyRef = useRef(null);

		useEffect(() => {
			if (typeof window === 'undefined') return;
			const currentKey = location
				? `${location.pathname}${location.search}`
				: null;
			if (prevLocationKeyRef.current === currentKey) return;
			const isInitialHydration = !searchHydrated;
			prevLocationKeyRef.current = currentKey;
			searchHydrated = true;
			if (isInitialHydration) return;
			const query = getSearchQuery(props);
			const action = isStorageDealsPage
				? actions.fetchStorageDealsResults
				: actions.fetchSearchResults;
			action(query).then(() => onSearchSuccess());
		});

		return (
			<WrappedComponent
				{...rest}
				onSearchSuccess={onSearchSuccess}
				actions={actions}
				match={match}
				location={location}
				isStorageDealsPage={isStorageDealsPage}
			/>
		);
	};

	WithSearchPageHandlers.propTypes = {
		onSearchSuccess: PropTypes.func,
		actions: PropTypes.object,
		match: PropTypes.object,
		location: PropTypes.shape({
			pathname: PropTypes.string,
			search: PropTypes.string,
		}),
		isStorageDealsPage: PropTypes.bool,
	};

	return WithSearchPageHandlers;
};

export default withSearchPageHandlers;
